import {Storage} from "@/services/Storage";
import axios from "axios";
import {responses} from "@/utils/responses";
import store from "@/store";
import * as Sentry from "@sentry/vue";

export default {
    name: 'user',
    state: {
        currentUser: undefined,
        users: [],
    },
    getters: {
        getCurrentUser: (state) => state.currentUser,

        getUsers: (state) => state.users,

        countNonLytSoftUsers : async (state) => {
            state.users = await Storage.getAllUsers();
            return state.users.filter(user=>user.Username !== 'Admin').length;
        },

        getUserByID: (state) => async (id) => {
            state.users = await Storage.getAllUsers();
            return state.users.find(u => u.id === id);
        },
        getSecurities: (state) => state.securities,
    },
    mutations: {
        setCurrentUser: async (state, data) => {
            if (!data) {
                data = await Storage.getCurrentUser();
            }
            state.currentUser = data;
        },
        setAllSecurities: async (state, data) => {
            if (!data) data = await Storage.getAllSecurities();
            else await Storage.setAllSecurities(data);
            state.securities = data;
        },
        setAllUsers: async (state, data) => {
            if (data) {
                await Storage.setAllUsers(data)
            } else {
                data = Storage.getAllUsers();
            }
            state.users = data
        },
    },
    actions: {
        /**
         * Records a successful login with the backend and attaches the
         * authenticated user to the Sentry SDK so all subsequent error and
         * performance events are tagged with the cashier / operator
         * identity. Identity is opt-in via `dataCollection.userInfo: false`
         * in `main.js`, so this is the only path that adds user context.
         *
         * @param {Object} context   Vuex action context (unused)
         * @param {string} username  Username that just authenticated
         * @returns {Promise<AxiosResponse>}
         */
        async recordlogin(context , username) {
            let device = await Storage.getDeviceSettings();
            device = device.PC;
            let store = await Storage.getDefaultStore();
            if (!store) return;
            const payload = {
                username,
                platform: 'DSRAPP',
                pc: device,
            }
            // Attach user identity to Sentry events. PII is otherwise off
            // (`dataCollection.userInfo: false` in main.js).
            Sentry.setUser({
                id: username,
                username: username,
            });
            return axios.post(serverApi + `supervisorOverrides/${store.StoreDB}/recordLogin`,payload)
        },
        /**
         * Records a logout with the backend and clears the Sentry user
         * context. Pairs with `recordlogin` — without the clear, Sentry
         * events from the next user of the device would be tagged with the
         * previous user's identity until the page is reloaded.
         *
         * @param {Object} context   Vuex action context (unused)
         * @param {string} username  Username that is logging out
         * @returns {Promise<AxiosResponse>}
         */
        async recordLogout(context , username) {
            // Drop the Sentry user identity. Events after this point have no
            // `user.*` fields until the next successful `recordlogin`.
            Sentry.setUser(null);
            let device = await Storage.getDeviceSettings();
            device = device.PC;
            let store = await Storage.getDefaultStore();
            if (!store) return;
            const payload = {
                username: username,
                platform: 'DSRAPP',
                pc: device,
            }
            return axios.post(serverApi + `supervisorOverrides/${store.StoreDB}/recordLogout`,payload)
        },

        getAllUsers: async (context) => {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.get(franchiseTomcatApi + `employee/${store.StoreDB}/getAll`)
                .then(({data}) => {
                    context.commit('setAllUsers', data);
                    return data;
                }).catch((err) => errorHandler.tomcatError(err))
        },

        loginByUserName: async (context , username)=>{
            let user = context.getters.getUsers.find(emp => emp.Username === username);
            if (user) {
                await Storage.setCurrentUser(user);
                context.commit(`setCurrentUser`, user);
                return true
            }
            return false
        },

        /**
         * Verifies a cashier PIN against the backend Argon2id endpoint and,
         * on success, promotes the matched employee to the current POS user.
         *
         * Replaces the previous client-side plaintext-PIN compare. The backend
         * `posAuth/{DB}/login` endpoint transparently auto-migrates any
         * still-plaintext employee row to `$argon2id$...` on the first
         * successful verify, so the call is safe against legacy data.
         *
         * Response payload contract:
         *   - `{ success: true, empId, username }` -> valid PIN; cache is updated
         *   - `{ success: false }`                  -> invalid PIN
         *   - HTTP 401/409                          -> invalid PIN / PIN collision
         *
         * Offline login is intentionally NOT supported. The PIN screen
         * (`PosLoginForm.vue`) blocks the call when `navigator.onLine` is
         * false, so reaching this action implies a live network.
         *
         * @param {Object} context  Vuex action context
         * @param {string} password Raw PIN entered by the cashier (will be trimmed)
         * @returns {Promise<{isValid: boolean, username: (string|null)}>}
         *   Preserves the legacy return shape that callers (PosLoginForm,
         *   loginByUserName chain) depend on.
         */
        posLogin: async (context, password) => {
            const trimmed = (password ?? '').trim();
            if (!trimmed) {
                return { isValid: false, username: null };
            }
            let store = await Storage.getDefaultStore();
            if (!store || !store.StoreDB) {
                return { isValid: false, username: null };
            }
            try {
                const { data: resp } = await axios.post(
                    franchiseTomcatApi + `posAuth/${store.StoreDB}/login`,
                    { password: trimmed }
                );
                if (!resp || !resp.success) {
                    return { isValid: false, username: null };
                }
                // Match the response against the cached roster to preserve
                // downstream shape (Storage.setCurrentUser, securities lookup, etc).
                const currentUser = context.getters.getUsers.find(
                    u => u.id === resp.empId
                    || (u.Username && resp.username
                        && u.Username.toLowerCase() === resp.username.toLowerCase())
                );
                if (currentUser) {
                    await Storage.setCurrentUser(currentUser);
                    context.commit('setCurrentUser', currentUser);
                    return { isValid: true, username: currentUser.Username };
                }
                // Cache miss — server told us who. Use the response fields.
                return { isValid: true, username: resp.username };
            } catch (err) {
                return { isValid: false, username: null };
            }
        },

        async editEmployee(context, payload) {
            let store = await Storage.getDefaultStore();
            return axios.put(franchiseTomcatApi + `employee/${store.StoreDB}/${payload.id}`, payload)
                .then(() => context.dispatch(`getAllUsers`))
                .catch((err) => errorHandler.tomcatError(err))
        },

        createEmployee(context, payload) {
            let store = context.getters.getDefaultStore;
            return axios.post(franchiseTomcatApi + `employee/${store.StoreDB}`, payload)
                .then(() => context.dispatch('getAllUsers'))
                .catch((err) => errorHandler.tomcatError(err))
        },

        forceCashUp: async () => {
            let store = await Storage.getDefaultStore();
            let currentUser = await Storage.getCurrentUser()
            return axios.get(franchiseTomcatApi + `cashup/${store.StoreDB}/forceCashup?user=` + currentUser.Username+'&pc='+localStorage.getItem('_capuid'))
                .then(({data}) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },

        async generateStoreOTP() {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi+`stores/${store.StoreDB}/generateOTP`)
                .then(({data}) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },
        /**
         * Gates a sensitive POS action (e.g. void, discount override) behind
         * the cashier's security group, and — if the cashier isn't authorised
         * — prompts for a supervisor PIN verified by the BackOffice
         * `empAuth/{DB}/login` Argon2id endpoint.
         *
         * Replaces the legacy client-side plaintext-PIN compare. The
         * supervisor PIN is now sent to the BackOffice tenant (not the
         * Franchise tenant) and the response is used to identify the
         * supervisor via the cached employee roster.
         *
         * @param {Object} context    Vuex action context
         * @param {string} security   The action being authorised
         *   (e.g. `'void'`, `'discount'`). Matched against
         *   `securities[group][security]`.
         * @returns {Promise<Object|boolean>}
         *   - `true` if the current user is authorised in their own group.
         *   - `{ allowed: true, supervisor, cashier }` if supervisor overrode.
         *   - `{ supervisor }` for void-class actions (chainable).
         *   - `false` if denied or no security group configured.
         */
        validateSecurityPermission: async (context, security) => {
            let securities = await Storage.getAllSecurities();
            let user = await Storage.getCurrentUser();
            let userType = user.Type;
            let group = securities.find(sec => sec.GroupName.toLowerCase() === userType.toLowerCase());
            if (!group) {
                await responses.showWarning(`No security group found`)
                return false;
            }
            if (!group[security]) {
                return Swal.fire({
                    title: `User not Allowed to ${security}`,
                    icon : "warning",
                    text : `Input supervisor password \n To Allow ${user.Username} to ${security}`,
                    input: "password",
                    showCloseButton : true,
                    showCancelButton: true,
                    confirmButtonText: "Authorize",
                    showLoaderOnConfirm : false,
                    preConfirm: async (password) => {
                        let store = await Storage.getDefaultStore();
                        try {
                            const { data: resp } = await axios.post(
                                backOfficeTomcatApi + `empAuth/${store.StoreDB}/login`,
                                { password }
                            );
                            if (!resp || !resp.empId) {
                                Swal.showValidationMessage(`Incorrect password`);
                                return false;
                            }
                            const supervisor = context.getters.getUsers.find(
                                u => u.id === resp.empId
                                || (u.Username && resp.username
                                    && u.Username.toLowerCase() === resp.username.toLowerCase())
                            );
                            if (!supervisor) {
                                Swal.showValidationMessage(`Supervisor not found in cached roster`);
                                return false;
                            }
                            const supervisorType = supervisor.Type;
                            const supervisorGroup = securities.find(sec =>
                                sec.GroupName.toLowerCase() === supervisorType.toLowerCase());
                            if (!supervisorGroup || !supervisorGroup[security]) {
                                Swal.showValidationMessage(`User ${supervisor.Username} not allowed to authorize ${security}`);
                                return false;
                            }
                            return {
                                allowed: true,
                                supervisor: supervisor.Username,
                                cashier: user.Username,
                            };
                        } catch (err) {
                            Swal.showValidationMessage(`Supervisor verification failed`);
                            return false;
                        }
                    },
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        let value = result.value;
                        if (value.allowed === true){
                            let store = await Storage.getDefaultStore();
                            axios.post(serverApi+`supervisorOverrides/${store.StoreDB}`,{
                                Supervisor : value.supervisor,
                                Cashier : value.cashier,
                                Reason : security,
                            })
                        }
                        return value;
                    }
                    return false;
                });

            }
            if (security.toString().toLowerCase().includes(`void`)){
                return {supervisor : user.Username}
            }
            return true;
        },
        validateSecurityPermissionNoPrompt: async (context, security) => {
            let securities = await Storage.getAllSecurities();
            let user = await Storage.getCurrentUser();
            let userType = user.Type;
            let group = securities.find(sec => sec.GroupName.toLowerCase() === userType.toLowerCase());
            if (!group) {
                await responses.showWarning(`No security group found`)
                return false;
            }
            return group[security];
        },
        async getAllSecurities(context) {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.get(franchiseTomcatApi + `empsecurity/${store.StoreDB}/getAll`)
                .then(({data}) => context.commit(`setAllSecurities`, data))
                .catch((err) => errorHandler.tomcatError(err))
        },
    }
}
